import { ICreditOffer } from "lib/types/global";
import { sortGenrepack } from "../../helper/genre-helper";
import { pkgNameNormalizeString } from "../../helper/text";
import { logAdapterEvent, logError } from "../../helper/logger";
import { getQualifiedCredits } from "../../helper/credit-qualification";

const ADAPTER_NAME = "packageCompareToolAdapter";
const fallbackInternetOffersData = [
  { PREMIER: 154.99 },
  { "Optimo Mas": 69.99 },
  { ENTERTAINMENT: 74.99 },
  { ULTIMATE: 109.99 },
  { CHOICE: 79.99 },
];

const fallbackGenrePrices = [
  { ATT_TV_MIESPANOL: 29.99 },
  { ATT_TV_MYSPORTS: 59.99 },
  { ATT_TV_MYSPORTS_EXTRA: 12.99 },
  { ATT_TV_MYENTERTAINMENT: 29.99 },
  { ATT_TV_MYCINEMA: 9.99 },
  { ATT_TV_MYNEWS: 44.99 },
];

interface IPackageCompareToolAdapeter {
  content: any;
  chnlListingPackages: any[];
  chnlListingChannels: any[];
  chnlListingMiniPacks: any[];
  queryParams?: any;
  genreOffers: any;
  svodGenreData: any;
  packageConfig: any;
  genrePackOfferIds?: any;
  onDemandPackageConfig: any;
  genreLocalChannelMapping: any;
  signatureExcludedLocalChannels?: any[];
  getOffersData: any;
  domName?: string;
  myFreeDTVConfig: any;
  embeddedSvods: any;
  dtviEmbeddedSvodOffers: any;
  genreNationals?: any;
  creditOffers?: ICreditOffer[];
}

export const PackageCompareToolAdapeter = ({
  content,
  chnlListingPackages,
  chnlListingChannels,
  chnlListingMiniPacks,
  queryParams,
  genreOffers,
  svodGenreData,
  packageConfig,
  genrePackOfferIds,
  onDemandPackageConfig,
  genreLocalChannelMapping,
  signatureExcludedLocalChannels,
  getOffersData,
  domName,
  myFreeDTVConfig,
  embeddedSvods,
  dtviEmbeddedSvodOffers,
  genreNationals,
  creditOffers,
}: IPackageCompareToolAdapeter): any => {
  try {
    logAdapterEvent(ADAPTER_NAME, "Adapter execution started", {
      queryParams,
    });
    /**
     * Map packages to pkg names, channel counts from packageConfig and BYOD prices from getOffers */
    const channelCounts = {};
    const packageNames = [];
    let gpNameIconMapping = {};
    const filteredByodPrices: { [key: string]: any } = {};
    
    const includePackages: string[] = [];
    const packages: any[] = [];
    const DTVI_PACKAGE_MAPPING: Record<string, string> = {};
    const rsnPackageMapping: Record<string, string> = {};
    const byodPrices = 
      (domName === "streamtv"
        ? genreOffers?.byod?.offerPrices
        : getOffersData?.offerPrices) || fallbackInternetOffersData;
    packageConfig?.filter((val) => val.type === "internet")?.forEach((pkg) => {
      if (!pkg?.id) return;
      packages.push(pkg);
      includePackages.push(pkg.id);
      const normalizedKey = pkgNameNormalizeString(pkg?.displayLabel)?.toUpperCase?.();
      if (normalizedKey) rsnPackageMapping[normalizedKey] = pkg.id;
      if (pkg?.queryparam) DTVI_PACKAGE_MAPPING[pkg.queryparam] = pkg.id;
      const pkgId = pkg?.id;
      const packageNameLabel = pkg?.displayLabel;
      const pkgName = pkgNameNormalizeString(packageNameLabel)?.replace(/\s/g, "-");
      packageNames.push({
          key: pkgId,
          value: pkgId,
          label: pkg?.richTextDisplayLabel || pkg?.displayLabel,
          pkgName: pkgName
        });

        channelCounts[pkgId] =
          (pkg?.channel?.displayCount || "") +
            (pkg?.channel?.signLetter || "") +
            " " +
            (pkg?.channel?.label || "") || "";
      const normalizedPkgKey = pkgNameNormalizeString(packageNameLabel)?.toUpperCase?.();
      byodPrices.some((byodPrice) => {
        if (!normalizedPkgKey) {
          return false;
        }
        if (byodPrice[normalizedPkgKey]) {
          filteredByodPrices[pkgId] = byodPrice[normalizedPkgKey];
          return true;
        }        
        return false;
      });
      // Generated by Copilot- Start - 2026-03-26 17:48:10
      // Defensive guard: log when a package ID could not be matched to any BYOD price entry.
      // This can happen if byodPrices keys use a different casing/spelling than the
      // normalizedKey derived from packageConfig.displayLabel (e.g. feed sends "OPTIMO MAS"
      // but normalizedKey resolves to "OPTIMO MÁS"). Logging here lets on-call engineers
      // identify casing mismatches without a silent price-drop in the UI.
      if (pkgId && !(pkgId in filteredByodPrices)) {
        logAdapterEvent(ADAPTER_NAME, `filteredByodPrices: no BYOD price matched for pkgId "${pkgId}" (normalizedKey: "${normalizedKey}"). Check byodPrices key casing vs packageConfig.displayLabel.`, { pkgId, normalizedKey });
      }
      // Generated by Copilot- End - 2026-03-26 17:48:10
    });    
    const genrePrices = genreOffers?.offerPrices || fallbackGenrePrices;    
    const svodMapping = genreOffers?.svodMapping || [];
    // Generated by Copilot- Start - 2026-02-28 00:55:29
    // Build on-demand svod mapping: map on-demand IDs to their base package's svod codes
    // e.g., ATT_TV_MYSPORTS_ORIGINAL -> svodMapping["ATT_TV_MYSPORTS"]
    const onDemandSvodMapping: Record<string, string[]> = {};
    const svodBaseKeys = Object.keys(svodMapping || {});
    onDemandPackageConfig?.forEach?.((pkg) => {
      const id = pkg?.id;
      if (!id) return;
      const matchKey = svodBaseKeys?.find((key) => id?.includes?.(key));
      if (matchKey) {
        onDemandSvodMapping[id] = svodMapping[matchKey] || [];
      }
    });
    const enrichedSvodMapping = { ...svodMapping, ...onDemandSvodMapping };
    // Generated by Copilot- End - 2026-02-28 00:55:29
    const showMyFreeChannels =
      myFreeDTVConfig?.enableForChannelCompareTool || false;


    
    const selectedGenrePackages = [];

    // Generated by Copilot- Start - 2026-02-27 20:00:16
    // Track on-demand overrides keyed by on-demand package ID, includes base ID for price lookups
    const onDemandOverrides: Record<string, any> = {};
    // Generated by Copilot- End - 2026-02-27 20:00:16

    /**
     * Use query param to determine initial genrePack(s) */
    const genreMapping = {};
    let selectedGenrePackOfferIds = {};
    if(queryParams?.genrePacks) {
      queryParams?.genrePacks?.split?.(",")?.forEach?.((pkgQueryParama) => {
        // Generated by Copilot- Start - 2026-03-04 18:42:47
        // Check if queryparam matches an on-demand package
        const onDemandMatch = onDemandPackageConfig?.find?.(
          (item) => item.queryparam === pkgQueryParama
        );
        if (onDemandMatch) {
          const offerIds = genrePackOfferIds?.[pkgQueryParama];
          selectedGenrePackOfferIds[pkgQueryParama] = typeof offerIds === 'string' ? offerIds.split(',') : (offerIds || []);
          // Push on-demand ID for channel lookups
          selectedGenrePackages.push(onDemandMatch?.id);
          genreMapping[onDemandMatch?.queryparam] = onDemandMatch?.id;
          // Resolve base package ID for price lookups 
          const baseMatch = packageConfig?.find(
            (item) => onDemandMatch?.id?.includes?.(item?.id)
          );
          onDemandOverrides[onDemandMatch?.id] = {
            ...onDemandMatch,
            basePriceKey: baseMatch?.id || onDemandMatch?.id
          };
        } else {
          // Try exact queryparam match in packageConfig
          let match = packageConfig?.find(
            (item) => item.queryparam === pkgQueryParama
          );
          // Fallback: if no exact match, find a base package whose queryparam is a prefix
          // e.g., queryparam "mysportsoriginal" starts with base "mysports" -> use ATT_TV_MYSPORTS
          if (!match) {
            match = packageConfig?.find(
              (item) => item?.queryparam && pkgQueryParama?.startsWith?.(item.queryparam)
            );
          }
          // Guard: only push if a valid match is found, skip unrecognized queryparams
          if (match?.id) {
            const offerIds = genrePackOfferIds?.[pkgQueryParama];
            selectedGenrePackOfferIds[pkgQueryParama] = typeof offerIds === 'string' ? offerIds.split(',') : (offerIds || []);
            selectedGenrePackages.push(match?.id);
            genreMapping[match?.queryparam] = match?.id;
          }
        }
        // Generated by Copilot- End - 2026-03-04 18:42:47
      })
    }
    else{
      packageConfig.forEach((item) => {
        if (item?.type === "genre" || item?.type === "genre-mini") {
          selectedGenrePackages.push(item.id);
          genreMapping[item.queryparam] = item.id;
          if(genrePackOfferIds?.[item.queryparam]) {
            selectedGenrePackOfferIds[item.queryparam] = typeof genrePackOfferIds[item.queryparam] === 'string' ? genrePackOfferIds[item.queryparam].split(',') : (genrePackOfferIds[item.queryparam] || []);
          }
        }
      });
    }
    const genreCreditOffers = getQualifiedCredits(creditOffers, selectedGenrePackOfferIds)
    /**
     * Calculate the Total Price and channel counts for selected genre packs */
    let genrePacksTotalPrice = 0;
    let genrePacksTotalChnlCount = 0;
    let genrePackIconMap = content?.genrePackIconMap?.[0];
    let genrePackChannelCounts = [];
    try {
      genrePackChannelCounts = content.genrePackChannelCounts
        ? JSON.parse(JSON.stringify(content.genrePackChannelCounts))
        : [];
    } catch (e) {
      logError(ADAPTER_NAME, e, {
        step: "Parsing genrePackChannelCounts"
      });
    }
    selectedGenrePackages.map((pack) => {
      // Generated by Copilot- Start - 2026-02-27 20:00:16
      // For on-demand packages, use the base ID for price lookups 
      const onDemandMatch = onDemandOverrides[pack];
      const priceKey = onDemandMatch?.basePriceKey || pack;
      const genrePrice = genrePrices.find((price) => priceKey in price);
      if (genrePrice) {
        const price = Number(genrePrice?.[priceKey]?.price);
        genrePacksTotalPrice += price;
      }
      const match = packageConfig?.find((item) => item.id === pack) ||
        packageConfig?.find((item) => item.id === priceKey);
      // Use on-demand labels/counts when the pack has an on-demand override from queryparam
      const shouldUseOnDemandLabel = !!onDemandMatch;
      const labelSource = shouldUseOnDemandLabel ? onDemandMatch : match;
      const channelSource = shouldUseOnDemandLabel ? onDemandMatch?.channel : match?.channel;
      // Generated by Copilot- End - 2026-02-27 20:00:16
      const isMiniPack = chnlListingMiniPacks?.some((mini) => {
        const miniPackKey = mini?.split(",")[1]?.trim();
        return pack?.replace("ATT_TV_", "") === miniPackKey;
      });
      if (channelSource?.displayCount) {
        genrePacksTotalChnlCount += Number(channelSource?.displayCount);
      }
      const channelCount =
        (isMiniPack && !content?.showMiniGPChannelCount) ||
        (!isMiniPack && !content?.showGPChannelCount)
          ? ""
          : (channelSource?.displayCount || "") +
              (channelSource?.signLetter || "") +
              " " +
              (channelSource?.label || "") || "";
      genrePackChannelCounts[0][pack] =
        channelCount !== " " ? channelCount : "";
      gpNameIconMapping[pack] = {
        name: labelSource?.richTextDisplayLabel || labelSource?.displayLabel,
        icon: genrePackIconMap[pack],
      };
    });
    // Avoid any floating point errors (ex: 57.99999999996)
    genrePacksTotalPrice = parseFloat(genrePacksTotalPrice.toFixed(2));
    genreCreditOffers.forEach((creditOffer) => {
      genrePacksTotalPrice -= creditOffer.creditAmount;
    });
    if (genrePacksTotalPrice < 0) {
      genrePacksTotalPrice = 0;
    }

    // Add name and icon for Other channels
    gpNameIconMapping["Other channels"] = {
      name: "Other channels",
      icon: genrePackIconMap["otherChannels"],
    };

    // Add channel counts, name and icon for MyFree DIRECTV
    if (showMyFreeChannels) {
      const match = packageConfig?.find((item) => item.id === "MYFREE_DTV");
      genrePackChannelCounts[0]["MYFREE_DTV"] =
        (match?.channel?.displayCount || "") +
        " " +
        (match?.channel?.label || "");
      gpNameIconMapping["MYFREE_DTV"] = {
        name: content?.myFreePackageData?.[0]?.packageName || "",
        icon: genrePackIconMap["MYFREE_DTV"],
      };
    }

    // Avoid any floating point errors (ex: 57.99999999996)
    genrePacksTotalPrice = parseFloat(genrePacksTotalPrice.toFixed(2));

    /**
     * Calculate the closest BYOD price to the genrePacksTotalPrice for initial DD selection */
    let closestInPrice = "ATT_TV_CHOICE";
    if (
      queryParams?.selectPackage &&
      includePackages.indexOf(
        rsnPackageMapping[queryParams?.selectPackage?.toUpperCase()]
      ) > -1
    ) {
      closestInPrice =
        rsnPackageMapping[queryParams?.selectPackage?.toUpperCase()];
    } else {
      // Select Optimo Mas if MiEspanol genre pack is selected for compare
      if (
        queryParams?.genrePacks
          ?.split?.(",")
          ?.map((queryParam) => queryParam?.toLowerCase())
          ?.includes?.("miespanol")
      ) {
        closestInPrice = "ATT_TV_OPTIMO_MAS";
      } else {
        Object.keys(filteredByodPrices)?.forEach?.((key) => {
          if (
            Math.abs(filteredByodPrices[key] - genrePacksTotalPrice) <
              Math.abs(
                filteredByodPrices[closestInPrice] - genrePacksTotalPrice
              ) &&
            key !== "ATT_TV_OPTIMO_MAS"
          ) {
            closestInPrice = key;
          }
        });
      }
    }

    /**
     * Get the list of channels included in the given genre packs */
    const availableChannels = [];
    const myFreeChannels = [];
    let genrePackChannels = {};
    // Generated by Copilot- Start - 2026-03-04 17:23:12
    // Combine packageConfig with onDemandPackageConfig so sortGenrepack recognizes on-demand IDs
    const combinedPackageConfig = [
      ...(packageConfig || []),
      ...(onDemandPackageConfig || [])
    ];
    let sortedGenreChannelctagoeries = sortGenrepack(
      selectedGenrePackages,
      combinedPackageConfig
    )?.filter(Boolean);
    // Generated by Copilot- End - 2026-03-04 17:23:12
    chnlListingChannels?.forEach?.((channel) => {
      let isGenrePack = false;

      sortedGenreChannelctagoeries?.forEach?.((genrePackage, ix) => {
        if (
          channel?.packageListSystem?.indexOf(
            sortedGenreChannelctagoeries[ix]
          ) > -1
        ) {
          isGenrePack = true;
          const genreCtgy = genrePackChannels[genrePackage] || [];
          genreCtgy.push(channel);
          genrePackChannels[genrePackage] = genreCtgy;
        }
        if (ix === sortedGenreChannelctagoeries.length - 1 && !isGenrePack) {
          if (
            channel?.packageListSystem?.indexOf("MYFREE_DTV") > -1 &&
            showMyFreeChannels
          ) {
            myFreeChannels.push(channel);
          } else {
            availableChannels.push(channel);
          }
          // break;
        }
      });
    });
    if (
      selectedGenrePackages.includes("ATT_TV_MYHOMETEAM") &&
      !genrePackChannels["ATT_TV_MYHOMETEAM"]
    ) {
      genrePackChannels["ATT_TV_MYHOMETEAM"] = [];
    }
    genrePackChannels = {
      ...genrePackChannels,
      // myFreeChannels will be empty if myFreeDTVConfig is not enabled
      ["MYFREE_DTV"]: myFreeChannels,
      ["Other channels"]: availableChannels,
    };
    delete genrePackChannels?.["ATT_TV_MYNEWS"];
    // Sort channels within each genre pack
    Object.keys(genrePackChannels)?.forEach?.((genrePack) => {
      genrePackChannels[genrePack].sort((a, b) =>
        a.channelNameLabel.localeCompare(b.channelNameLabel)
      );
    });
    const sortedGenrePackChannels = {};
    sortedGenreChannelctagoeries.forEach((genrePackage) => {
      if (genrePackChannels[genrePackage]) {
        sortedGenrePackChannels[genrePackage] = genrePackChannels[genrePackage];
      }
    });

    sortedGenrePackChannels["Other channels"] = availableChannels;
    // myFreeChannels will be empty if myFreeDTVConfig is not enabled
    sortedGenrePackChannels["MYFREE_DTV"] = myFreeChannels;    

    const reducedEmbeddedSvods = embeddedSvods?.map((svod) => ({
      name: svod?.name || "",
      offerCode: svod?.offerCode || "",
      packageCompareToolData: svod?.packageCompareToolData || {}
    })) || [];

    const handleGetReducedSvodOffers = (dtviEmbeddedSvodOffers) => {
      try {
        const reducedSvodOffers = {};
        Object.entries(DTVI_PACKAGE_MAPPING || {}).forEach(([pkgKey, pkgId]) => {
          dtviEmbeddedSvodOffers?.[pkgKey]?.forEach((svod) => {
            if(!reducedSvodOffers[svod?.code]) {
              reducedSvodOffers[svod?.code] = [];
            }
            reducedSvodOffers[svod?.code]?.push(pkgId);
          })
        })
        return reducedSvodOffers;
      } catch (error) {
        console.error("Error in handleGetReducedSvodOffers:", error);
        return {};
      }
    };

    const handleGetSignatureSvodNameMapping = (dtviEmbeddedSvodOffers) => {
      try {
        const signatureSvodNameMapping = {};
        Object.entries(DTVI_PACKAGE_MAPPING || {}).forEach(([pkgKey, pkgId]) => {
          dtviEmbeddedSvodOffers?.[pkgKey]?.forEach((svod) => {
            if (svod?.code && svod?.name) {
              signatureSvodNameMapping[svod?.code] = svod?.name;
            }
          })
        })
        return signatureSvodNameMapping;
      } catch (error) {
        console.error("Error in handleGetSignatureSvodNameMapping:", error);
        return {};
      }
    };

    const handleGetGenreSvodCodes = (svodMapping) => {
      try {
        const genreSvodCodes = [];
        Object.entries(svodMapping || {}).forEach(([genreKey, svodList]) => {
          if (Array.isArray(svodList)) {
            genreSvodCodes.push(...svodList);
          }
        });
        return genreSvodCodes;
      } catch (error) {
        console.error("Error in handleGetGenreSvodCodes:", error);
        return [];
      }
    };

    return {
      props: {
        ...content,
        genrePackChannels: sortedGenrePackChannels,
        sortedGenreChannelctagoeries,
        selectedPackage: closestInPrice,
        packageNames,
        channelCounts,
        byodPrices: filteredByodPrices,
        genrePacksTotalPrice,
        genrePacksTotalChnlCount,
        gpNameIconMapping,
        genreLocalChannelMapping,
        signatureExcludedLocalChannels,
        offersData: getOffersData,
        svodGenreData,
        genreMapping,
        svodMapping: enrichedSvodMapping,
        queryParams,
        domName,
        genrePackChannelCounts,
        embeddedSvods: reducedEmbeddedSvods,
        reducedSvodOffers: handleGetReducedSvodOffers(dtviEmbeddedSvodOffers),
        signatureSvodNameMapping: handleGetSignatureSvodNameMapping(dtviEmbeddedSvodOffers),
        genreSvodCodes: handleGetGenreSvodCodes(enrichedSvodMapping),
        genreNationals,
      },
      type: "PACKAGECOMPARETOOL",
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {});
  }
};

interface IDynamicPackageCompareToolAdapeterArgs
  extends IPackageCompareToolAdapeter {}

export function dynamicPackageCompareToolAdapter(
  args: IDynamicPackageCompareToolAdapeterArgs
) {
  try {
    logAdapterEvent(ADAPTER_NAME, "Dynamic Adapter execution started", {
      queryParams: args?.queryParams,
    });
    return PackageCompareToolAdapeter(args);
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      queryParams: args?.queryParams,
    });
    return args?.content;
  }
}